import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Download, FileText, Lock, Printer, Plus, X } from '../../lib/icons';
import { useAuth } from '../../lib/auth';
import { fmt, StatBlock } from '../shared/badges';
import { fetchFinances } from '../../lib/dataProvider';
import {
  CATEGORIE_CCA,
  cleIdempotence,
  contrepasserMouvementCca,
  enregistrerMouvementCca,
  fetchCandidatsTresorerieCca,
  fetchEtatCca,
  fetchReleveCca,
  initialiserAssocies,
  peutRepriseCca,
  peutSaisirCca,
} from '../../lib/cca';
import { SoldesInitiauxCCA } from './SoldesInitiauxCCA';

const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const MODES = ['Virement', 'Chèque', 'Espèces', 'Traite'];

const vide = (aujourdhui) => ({
  contact_id: '',
  nature: 'apport',
  montant: '',
  compte_id: '',
  date_effective: aujourdhui,
  mode: 'Virement',
  reference: '',
  libelle: '',
  // Contrepartie de trésorerie : créée par le serveur, ou écriture existante rattachée.
  source_tresorerie: 'creer',
  lien_tresorerie: '',
});

// Finances → Comptes Courants d'Associés : financement, jamais exploitation.
export const ComptesAssocies = () => {
  const { user } = useAuth();
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [f, setF] = useState(vide(aujourdhui));
  const [ouvert, setOuvert] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [releve, setReleve] = useState(null);
  const [candidats, setCandidats] = useState([]);
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const saisie = peutSaisirCca(user);
  const admin = peutRepriseCca(user);

  const load = useCallback(() => {
    fetchEtatCca(user).then((d) => {
      if (d) setEtat(d);
      else setErreur('Accès au relevé CCA non autorisé pour ce profil.');
    });
    fetchFinances().then((fin) => setComptes(fin.soldes ?? fin.comptes ?? []));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Candidats de contrepartie : rafraîchis dès que le sens, le compte ou le montant change.
  useEffect(() => {
    if (f.source_tresorerie !== 'rattacher' || !f.compte_id || !(Number(f.montant) > 0)) {
      setCandidats([]);
      return;
    }
    fetchCandidatsTresorerieCca(
      { nature: f.nature, compte_id: f.compte_id, montant: Number(f.montant) },
      user
    ).then(setCandidats);
  }, [f.source_tresorerie, f.nature, f.compte_id, f.montant, user]);

  const associe = etat?.comptes?.find((c) => c.contact_id === f.contact_id) ?? null;
  const compteTresorerie = comptes.find((c) => c.id === f.compte_id) ?? null;
  const montant = Number(f.montant) || 0;
  const soldeAvant = associe?.solde ?? 0;
  const soldeApres = f.nature === 'apport' ? soldeAvant + montant : soldeAvant - montant;

  const dansPeriode = (d) => (!du || String(d) >= du) && (!au || String(d) <= au);
  const periode = useMemo(() => {
    const lignes = (etat?.comptes ?? []).flatMap((c) => c.mouvements_liste ?? []);
    return lignes;
  }, [etat]);

  const enregistrer = async (e) => {
    e.preventDefault();
    if (!f.contact_id) return toast.error('Choisissez l’associé concerné.');
    if (!(montant > 0)) return toast.error('Saisissez un montant positif.');
    if (!f.compte_id) return toast.error('Choisissez la banque ou la caisse.');
    const lien =
      f.source_tresorerie === 'rattacher'
        ? candidats.find((c) => `${c.bucket}:${c.id}` === f.lien_tresorerie)
        : null;
    if (f.source_tresorerie === 'rattacher' && !lien)
      return toast.error('Choisissez l’écriture de trésorerie à rattacher (même compte, montant exact).');
    const payload = {
      ...f,
      montant,
      statut: 'valide',
      tresorerie_existante: lien ? { bucket: lien.bucket, id: lien.id } : null,
    };
    setOccupe(true);
    const { error, data } = await enregistrerMouvementCca(
      { ...payload, cle_idempotence: cleIdempotence(payload) },
      user
    );
    setOccupe(false);
    if (error) return toast.error(error);
    toast.success(
      data.deja_enregistre
        ? `Mouvement déjà enregistré (${data.id}) — aucun doublon créé.`
        : `${f.nature === 'apport' ? 'Apport' : 'Remboursement'} de ${fmt(montant, 3)} TND enregistré : ${
            lien
              ? `rattaché à l’écriture de trésorerie ${lien.id} (aucune seconde écriture créée)`
              : f.nature === 'apport'
                ? 'la trésorerie augmente'
                : 'la trésorerie diminue'
          } et la dette CCA est mise à jour. Comptes fournisseur et client inchangés.`
    );
    setF(vide(aujourdhui));
    setOuvert(false);
    load();
  };

  const contrepasser = async (m) => {
    const motif = window.prompt('Motif de la contrepassation (obligatoire) :');
    if (!motif || motif.trim().length < 3) return;
    const { error, data } = await contrepasserMouvementCca(m.id, motif.trim(), user);
    if (error) return toast.error(error);
    toast.success(`Contrepassation ${data.id} enregistrée — le mouvement d’origine reste intact.`);
    ouvrirReleve(m.contact_id);
    load();
  };

  const ouvrirReleve = async (contactId) => {
    const r = await fetchReleveCca(contactId, user);
    if (!r) return toast.error('Relevé indisponible pour ce profil.');
    setReleve(r);
  };

  const rattacher = async () => {
    const { error, data } = await initialiserAssocies(user);
    if (error) return toast.error(error);
    const intro = data.introuvables?.length ?? 0;
    toast[intro ? 'warning' : 'success'](
      `${data.rattaches.length} fiche(s) rattachée(s)${intro ? ` · ${intro} fiche(s) introuvable(s) sur ce poste (aucun contact créé)` : ''}`
    );
    load();
  };

  const exporter = () => {
    const lignes = [
      'Associe;Code;Solde CCA (TND);Apports (TND);Remboursements (TND);Solde initial;Etat',
      ...(etat?.comptes ?? []).map((c) =>
        [c.nom, c.code ?? '', fmt(c.solde, 3), fmt(c.apports, 3), fmt(c.remboursements, 3), c.solde_initial == null ? 'à renseigner' : fmt(c.solde_initial, 3), c.provisoire ? 'provisoire' : 'définitif'].join(';')
      ),
    ];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`\ufeff${lignes.join('\n')}`], { type: 'text/csv;charset=utf-8;' }));
    a.download = 'comptes-courants-associes.csv';
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Export CSV des CCA');
  };

  if (erreur)
    return (
      <p data-testid="cca-non-autorise" className="border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
        {erreur} La permission « Consultation CCA » est attribuable par l’administrateur.
      </p>
    );
  if (!etat) return <p className="px-4 py-6 text-sm text-slate-500">Chargement des comptes courants d’associés…</p>;

  return (
    <div data-testid="cca-module" className="space-y-5">
      <div className="border-l-4 border-slate-900 bg-slate-50 px-4 py-3">
        <p className="text-sm font-bold text-slate-900">Financement — Comptes courants d’associés</p>
        <p className="mt-0.5 text-xs text-slate-600">
          Convention : <strong>solde positif = montant que l’entreprise doit à l’associé</strong>. Les apports et
          remboursements affectent la banque ou la caisse concernée et la dette envers l’associé. Ils ne sont ni du
          chiffre d’affaires, ni une charge, ni une augmentation de capital, et n’ont aucun effet sur le CMP, le CRI, les
          stocks ou les droits en huile. Soldes calculés le {String(etat.calcule_le).slice(0, 16).replace('T', ' ')}.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <StatBlock testid="cca-total-du" titre="Total dû aux associés" accent="border-l-slate-900" items={[['Montant (TND)', fmt(etat.total_du, 3)]]} />
        <StatBlock testid="cca-total-apports" titre="Apports (période)" accent="border-l-emerald-600" items={[['Montant (TND)', fmt(etat.total_apports, 3)]]} />
        <StatBlock testid="cca-total-remb" titre="Remboursements (période)" accent="border-l-red-500" items={[['Montant (TND)', fmt(etat.total_remboursements, 3)]]} />
        <StatBlock
          testid="cca-variation"
          titre="Variation nette de trésorerie"
          accent="border-l-sky-600"
          items={[['Montant (TND)', `${etat.variation_tresorerie >= 0 ? '+' : '−'}${fmt(Math.abs(etat.variation_tresorerie), 3)}`]]}
        />
      </div>

      {etat.attendus_manquants?.length > 0 && (
        <div data-testid="cca-associes-manquants" className="flex flex-wrap items-center gap-3 border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle size={16} className="text-amber-700" />
          <p className="flex-1 text-xs text-amber-900">
            Fiches associés non rattachées sur ce poste :{' '}
            <strong>{etat.attendus_manquants.map((a) => `${a.designation ?? a.nom} (${a.code})`).join(', ')}</strong>. Aucun
            contact de remplacement n’est créé : le rattachement se fait sur les fiches existantes (production).
          </p>
          {admin && (
            <button type="button" data-testid="cca-rattacher" onClick={rattacher} className="h-9 border border-amber-500 px-3 text-xs font-bold text-amber-900 hover:bg-amber-100">
              Rattacher les fiches existantes
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {saisie && (
          <>
            <button
              type="button"
              data-testid="cca-nouvel-apport"
              onClick={() => {
                setF({ ...vide(aujourdhui), nature: 'apport' });
                setOuvert(true);
              }}
              className="flex h-11 items-center gap-2 border-2 border-emerald-700 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-emerald-800 hover:bg-emerald-700 hover:text-white"
            >
              <Plus size={15} /> Enregistrer un apport
            </button>
            <button
              type="button"
              data-testid="cca-nouveau-remboursement"
              onClick={() => {
                setF({ ...vide(aujourdhui), nature: 'remboursement' });
                setOuvert(true);
              }}
              className="flex h-11 items-center gap-2 border-2 border-red-700 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-red-800 hover:bg-red-700 hover:text-white"
            >
              Rembourser un associé
            </button>
          </>
        )}
        <button type="button" data-testid="cca-export" onClick={exporter} className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 hover:bg-slate-900 hover:text-white">
          <Download size={15} /> Export CSV
        </button>
      </div>

      <section className="border border-slate-200 bg-white">
        <table className="w-full border-collapse text-left" data-testid="cca-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {['Associé', 'Code', 'Solde initial', 'Solde CCA (TND)', 'Sens', 'Mouvements', 'Relevé'].map((c) => (
                <th key={c} className={th}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {etat.comptes.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500">
                  Aucun compte courant d’associé rattaché sur ce poste.
                </td>
              </tr>
            ) : (
              etat.comptes.map((c) => (
                <tr key={c.contact_id} data-testid={`cca-row-${c.contact_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm font-semibold text-slate-900">{c.nom}</td>
                  <td className="px-3 py-2 font-data text-xs text-slate-500">{c.code ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.solde_initial == null ? (
                      <span data-testid={`cca-initial-manquant-${c.contact_id}`} className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-data text-[10px] uppercase tracking-[0.08em] text-amber-800">
                        Solde initial à renseigner
                      </span>
                    ) : (
                      <span className="font-data text-slate-700">{fmt(c.solde_initial, 3)} TND</span>
                    )}
                  </td>
                  <td data-testid={`cca-solde-${c.contact_id}`} className="tabular px-3 py-2 font-data text-sm font-bold text-slate-900">
                    {fmt(c.solde, 3)}
                    {c.provisoire && <span className="ml-1 font-data text-[10px] uppercase text-amber-700">provisoire</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {c.solde > 0 ? 'L’entreprise doit ce montant à l’associé' : c.solde < 0 ? 'L’associé doit ce montant à l’entreprise' : 'Compte soldé'}
                  </td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{c.mouvements}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`cca-releve-${c.contact_id}`}
                      onClick={() => ouvrirReleve(c.contact_id)}
                      className="inline-flex items-center gap-1 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-900 hover:text-white"
                    >
                      <FileText size={13} /> Relevé
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {admin && <SoldesInitiauxCCA comptes={etat.comptes} onDone={load} />}

      {ouvert && (
        <div data-testid="cca-form-overlay" className="fixed inset-0 z-[70] flex items-start justify-center overflow-auto bg-slate-900/60 p-4">
          <form onSubmit={enregistrer} data-testid="cca-form" className="w-full max-w-2xl border border-slate-200 bg-white">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
                  {f.nature === 'apport' ? 'Apport en compte courant d’associé' : 'Remboursement de compte courant d’associé'}
                </h2>
                <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  Mouvement de financement — {CATEGORIE_CCA}
                </p>
              </div>
              <button type="button" data-testid="cca-form-close" onClick={() => setOuvert(false)} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
              <div>
                <label className={label}>Associé</label>
                <select data-testid="cca-associe" value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })} className={field}>
                  <option value="">— Choisir —</option>
                  {etat.comptes.map((c) => (
                    <option key={c.contact_id} value={c.contact_id}>{c.nom}{c.code ? ` (${c.code})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Nature du mouvement</label>
                <select data-testid="cca-nature" value={f.nature} onChange={(e) => setF({ ...f, nature: e.target.value })} className={field}>
                  <option value="apport">Apport de financement reçu</option>
                  <option value="remboursement">Remboursement payé à l’associé</option>
                </select>
              </div>
              <div>
                <label className={label}>Montant (TND)</label>
                <input data-testid="cca-montant" type="number" step="0.001" min="0" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} className={field} />
              </div>
              <div>
                <label className={label}>Banque ou caisse</label>
                <select data-testid="cca-compte" value={f.compte_id} onChange={(e) => setF({ ...f, compte_id: e.target.value })} className={field}>
                  <option value="">— Choisir —</option>
                  {comptes.map((c) => (
                    <option key={c.id} value={c.id}>{c.nom}{c.solde != null ? ` — ${fmt(c.solde, 3)} TND` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Date effective</label>
                <input data-testid="cca-date" type="date" value={f.date_effective} onChange={(e) => setF({ ...f, date_effective: e.target.value })} className={field} />
              </div>
              <div>
                <label className={label}>Mode de règlement</label>
                <select data-testid="cca-mode" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className={field}>
                  {MODES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Référence (chèque, virement…)</label>
                <input data-testid="cca-reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className={field} />
              </div>
              <div>
                <label className={label}>Libellé</label>
                <input data-testid="cca-libelle" value={f.libelle} onChange={(e) => setF({ ...f, libelle: e.target.value })} className={field} placeholder="Apport de financement — campagne 2026" />
              </div>
              <div>
                <label className={label}>Contrepartie de trésorerie</label>
                <select
                  data-testid="cca-source-tresorerie"
                  value={f.source_tresorerie}
                  onChange={(e) => setF({ ...f, source_tresorerie: e.target.value, lien_tresorerie: '' })}
                  className={field}
                >
                  <option value="creer">
                    {f.nature === 'apport' ? 'Créer l’alimentation de caisse' : 'Créer la sortie de caisse'}
                  </option>
                  <option value="rattacher">Rattacher une écriture déjà saisie</option>
                </select>
              </div>
              {f.source_tresorerie === 'rattacher' && (
                <div className="sm:col-span-2">
                  <label className={label}>
                    Écriture de trésorerie existante, non affectée (montant exact, même compte)
                  </label>
                  <select
                    data-testid="cca-lien-tresorerie"
                    value={f.lien_tresorerie}
                    onChange={(e) => setF({ ...f, lien_tresorerie: e.target.value })}
                    className={field}
                  >
                    <option value="">— Choisir —</option>
                    {candidats.map((c) => (
                      <option key={`${c.bucket}:${c.id}`} value={`${c.bucket}:${c.id}`}>
                        {c.date} · {c.id} · {fmt(c.montant, 3)} TND · {c.libelle || c.piece || '—'}
                      </option>
                    ))}
                  </select>
                  {!candidats.length && (
                    <p data-testid="cca-aucun-candidat" className="mt-1 text-[11px] text-amber-700">
                      Aucune écriture rattachable : le sens, le compte et un montant exactement égal sont
                      exigés, et une écriture déjà affectée (CCA, conversion, Passerelle) est exclue.
                    </p>
                  )}
                </div>
              )}
              <div className="sm:col-span-2">
                <label className={label}>Justificatif (facultatif)</label>
                <input
                  data-testid="cca-justificatif"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2_000_000) return toast.error('Justificatif trop volumineux (max 2 Mo).');
                    const reader = new FileReader();
                    reader.onload = () => setF((p) => ({ ...p, justificatif: String(reader.result), justificatif_nom: file.name }));
                    reader.readAsDataURL(file);
                  }}
                  className="mt-1.5 w-full border border-slate-300 bg-slate-50 px-3 py-2 text-sm"
                />
                {f.justificatif_nom && <p className="mt-1 font-data text-[11px] text-slate-600">{f.justificatif_nom}</p>}
              </div>
            </div>

            {f.contact_id && montant > 0 && (
              <div data-testid="cca-confirmation" className="mx-5 mb-4 border border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-800">
                Vous enregistrez un <strong>{f.nature === 'apport' ? 'apport' : 'remboursement'}</strong> de{' '}
                <strong>{fmt(montant, 3)} TND</strong> {f.nature === 'apport' ? 'de' : 'à'} <strong>{associe?.nom}</strong>.{' '}
                {compteTresorerie?.nom ?? 'Le compte choisi'} <span className={f.nature === 'apport' ? 'font-bold text-emerald-700' : 'font-bold text-red-700'}>
                  {f.nature === 'apport' ? 'augmente' : 'diminue'} de {fmt(montant, 3)} TND
                </span>{' '}
                et la dette CCA {f.nature === 'apport' ? 'augmente' : 'diminue'} du même montant. Ses comptes fournisseur et
                client restent inchangés.
                <div className="mt-2 flex flex-wrap gap-4 font-data">
                  <span>Solde CCA avant : <strong>{fmt(soldeAvant, 3)} TND</strong></span>
                  <span>Solde CCA après : <strong>{fmt(soldeApres, 3)} TND</strong></span>
                  {associe?.provisoire && <span className="text-amber-700">Solde provisoire — solde initial non repris</span>}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 border-t border-slate-200 px-5 py-4">
              <button
                type="submit"
                data-testid="cca-valider"
                disabled={occupe}
                className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white disabled:opacity-50"
              >
                {occupe ? 'Enregistrement…' : 'Valider le mouvement'}
              </button>
            </div>
          </form>
        </div>
      )}

      {releve && (
        <div data-testid="cca-releve-overlay" className="fixed inset-0 z-[70] overflow-auto bg-slate-900/60 p-4 print:static print:bg-white print:p-0">
          <div className="mx-auto w-full max-w-4xl bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 print:hidden">
              <p className="font-heading text-sm font-bold text-slate-900">
                Relevé CCA — {releve.nom} {releve.code ? `(${releve.code})` : ''}
              </p>
              <div className="flex gap-2">
                <button type="button" data-testid="cca-releve-imprimer" onClick={() => window.print()} className="flex h-10 items-center gap-2 bg-slate-900 px-4 text-sm font-bold text-white">
                  <Printer size={15} /> Imprimer
                </button>
                <button type="button" data-testid="cca-releve-close" onClick={() => setReleve(null)} className="border border-slate-300 p-2 text-slate-600 hover:bg-slate-900 hover:text-white">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-slate-900 pb-3">
                <div>
                  <p className="font-heading text-base font-black uppercase tracking-[0.08em]">Compte courant d’associé</p>
                  <p className="font-data text-[11px] text-slate-600">
                    Convention : solde positif = dette de l’entreprise envers l’associé
                  </p>
                </div>
                <div className="text-right">
                  <p className={label}>Solde CCA</p>
                  <p data-testid="cca-releve-solde" className="tabular font-data text-xl font-bold text-slate-900">{fmt(releve.solde, 3)} TND</p>
                  {releve.provisoire && <p className="font-data text-[11px] text-amber-700">{releve.mention}</p>}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-end gap-3 print:hidden">
                <div>
                  <label className={label}>Du</label>
                  <input type="date" data-testid="cca-releve-du" value={du} onChange={(e) => setDu(e.target.value)} className={field} />
                </div>
                <div>
                  <label className={label}>Au</label>
                  <input type="date" data-testid="cca-releve-au" value={au} onChange={(e) => setAu(e.target.value)} className={field} />
                </div>
              </div>

              {releve.chevauchements?.length > 0 && (
                <p data-testid="cca-chevauchement" className="mt-3 border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                  <AlertTriangle size={12} className="mr-1 inline" /> {releve.chevauchements.length} mouvement(s) antérieur(s)
                  ou du même jour que la date de reprise : vérifiez l’absence de double comptage ({releve.chevauchements.join(', ')}).
                </p>
              )}

              <table className="mt-4 w-full border-collapse text-left" data-testid="cca-releve-table">
                <thead>
                  <tr className="bg-slate-100">
                    {['Date', 'Nature', 'Libellé', 'Réf.', 'Banque / Caisse', 'Entrée', 'Sortie', 'Solde', 'Statut', ''].map((c, i) => (
                      <th key={c || i} className="border border-slate-300 px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-600">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {releve.lignes.filter((l) => dansPeriode(l.date ?? l.date_effective)).length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">Aucun mouvement sur la période.</td>
                    </tr>
                  ) : (
                    releve.lignes
                      .filter((l) => dansPeriode(l.date ?? l.date_effective))
                      .map((l) => (
                        <tr key={l.id} data-testid={`cca-ligne-${l.id}`} className="border-b border-slate-100">
                          <td className="border border-slate-200 px-2 py-1.5 font-data text-[11px]">{l.date ?? l.date_effective}</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-[11px] capitalize">{String(l.nature).replace('_', ' ')}</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-[11px]">{l.libelle}</td>
                          <td className="border border-slate-200 px-2 py-1.5 font-data text-[11px]">{l.reference ?? '—'}</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-[11px]">{l.compte_nom ?? (l.nature === 'reprise_initiale' ? 'Aucun flux de trésorerie' : '—')}</td>
                          <td className="tabular border border-slate-200 px-2 py-1.5 text-right font-data text-[11px] font-semibold text-emerald-700">{l.entree ? fmt(l.entree, 3) : ''}</td>
                          <td className="tabular border border-slate-200 px-2 py-1.5 text-right font-data text-[11px] font-semibold text-red-700">{l.sortie ? fmt(l.sortie, 3) : ''}</td>
                          <td className="tabular border border-slate-200 px-2 py-1.5 text-right font-data text-[11px] font-bold">{l.solde == null ? '—' : fmt(l.solde, 3)}</td>
                          <td className="border border-slate-200 px-2 py-1.5 text-[10px] uppercase">
                            {l.statut}
                            {l.contrepasse_par && <span className="ml-1 text-slate-500">→ {l.contrepasse_par}</span>}
                          </td>
                          <td className="border border-slate-200 px-2 py-1.5 text-right print:hidden">
                            {admin && l.statut === 'valide' && l.nature !== 'contrepassation' && l.nature !== 'reprise_initiale' && !l.contrepasse_par && (
                              <button type="button" data-testid={`cca-contrepasser-${l.id}`} onClick={() => contrepasser(l)} className="border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-900 hover:text-white">
                                Contrepasser
                              </button>
                            )}
                            {l.justificatif && (
                              <a data-testid={`cca-justificatif-${l.id}`} href={l.justificatif} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-1 border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700">
                                <Lock size={10} /> Pièce
                              </a>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
              <p className="mt-3 font-data text-[10px] text-slate-500">
                {periode.length === 0 ? '' : ''}Mouvements de financement — exclus du chiffre d’affaires, des charges
                d’exploitation, du CMP et du CRI. Contrepartie unique dans Banque/Caisse.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
